import { createSlug } from '../../utils/helpers';
import { getEmployee, listEmployees, upsertEmployee } from '../../api/companyRecords';

export function getEmployeeDisplayName(employee) {
  if (!employee) return '';
  const fromParts = `${employee.firstName || ''} ${employee.lastName || ''}`.trim();
  return employee.displayName || employee.name || fromParts || String(employee.email || '').trim();
}

/** Prefer stored slug, then record id — for /employees/profile URLs. */
export function getCompanyEmployeeUrlSegment(employee) {
  if (!employee) return '';
  const slug = String(employee.slug || '').trim();
  if (slug) return slug;
  return employee.id ? String(employee.id) : '';
}

export function assignUniqueEmployeeSlug(displayName, existingEmployees = [], excludeId = null) {
  const base = createSlug(displayName) || 'employee';
  const taken = new Set(
    existingEmployees
      .filter((employee) => employee?.id && employee.id !== excludeId)
      .map((employee) => String(employee.slug || '').trim())
      .filter(Boolean)
  );

  let slug = base;
  let suffix = 2;
  while (taken.has(slug)) {
    slug = `${base}-${suffix}`;
    suffix += 1;
  }
  return slug;
}

export function buildEmployeeSlugFields(employee, existingEmployees = [], excludeId = null) {
  const displayName = getEmployeeDisplayName(employee);
  return {
    slug: assignUniqueEmployeeSlug(displayName, existingEmployees, excludeId),
  };
}

export async function resolveCompanyEmployee(companyId, param, ownerId = null) {
  if (!companyId || param == null || String(param).trim() === '') return null;

  const paramStr = String(param).trim();
  try {
    const direct = await getEmployee({ companyId, id: paramStr });
    if (direct?.id) {
      return { employee: direct, employeeId: direct.id, isOwnerProfile: direct.id === ownerId };
    }
  } catch (error) {
    if (error.status && error.status !== 404) throw error;
  }

  const employees = await listEmployees({ companyId });
  const bySlug = employees.find((employee) => String(employee.slug || '').trim() === paramStr);
  if (bySlug) {
    return { employee: bySlug, employeeId: bySlug.id, isOwnerProfile: bySlug.id === ownerId };
  }

  if (ownerId && paramStr === ownerId) {
    const owner = employees.find((employee) => employee.id === ownerId);
    if (owner) {
      return { employee: owner, employeeId: ownerId, isOwnerProfile: true };
    }
  }

  return null;
}

export async function ensureCompanyEmployeeSlug(companyId, employeeId, employeeData) {
  if (!employeeId) return '';
  const resolvedCompanyId = companyId || employeeData?.companyId;
  if (!resolvedCompanyId) return String(employeeData?.slug || '').trim();

  const existingSlug = String(employeeData?.slug || '').trim();
  if (existingSlug) return existingSlug;

  const existingEmployees = await listEmployees({ companyId: resolvedCompanyId });
  const slug = assignUniqueEmployeeSlug(
    getEmployeeDisplayName(employeeData),
    existingEmployees,
    employeeId
  );
  await upsertEmployee({
    companyId: resolvedCompanyId,
    id: employeeId,
    employee: { slug, updatedAt: new Date().toISOString() },
  });
  return slug;
}
